import React, {Component} from "react";
import {Link} from "react-router-dom";
import {useTranslation, withTranslation} from 'react-i18next';
import {APIClient} from "../../utils/APIClient";
import {API_URL} from "../../settings/local_settings";
import {AccessService} from "../../utils/AccessService";
import {useUserModules} from "../../context/UserModulesContext";
import {AgencyHeader} from "./AgencyHeader";
import {AgencyTabs} from "./AgencyTabs";
import {ConnectionsSubTabs, CONNECTIONS_TAB_EXPORT} from "./ConnectionsSubTabs";
import {LookerCCComponent} from "./looker_cc/LookerCCComponent";
import {DataNodeComponent} from "./data_node/DataNodeComponent";
import {ChannelLogo} from "../channels/ChannelLogo";
import {SHEET_CHANNEL} from "../channels/channels";
import {LoadErrorState} from "../elements/LoadErrorState";

import {withRouterParams} from "../../utils/withRouterParams";

/**
 * La pestaña «Exportación» de Conexiones (D9 del recorrido de la 2.1, 02/10): todo lo que SACA
 * datos de Dashbo hacia otra herramienta. Hasta esa tanda esta ruta era la segunda solapa de la
 * exportación a planillas, y el conector de Data Studio aparecía también al pie de Conexiones: la
 * misma caja en dos pantallas. Ahora vive sólo acá, y las planillas entran como una tarjeta que
 * lleva a su pantalla.
 */
class AgencyConnectionsExportContent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            // Error de la CARGA (null = sin error). Sin esto, un fallo del GET dejaba las dos cajas
            // con el spinner para siempre.
            loadError: null,
        };
        this.retryLoad = this.retryLoad.bind(this);
    }

    redirectToAgencyRegisterPage() {
        this.props.history.push('/agency-register');
    }

    componentDidMount() {
        this.updateConnectionsInformation();
    }

    retryLoad() {
        // Vuelve al loader antes de pedir de nuevo, así el clic se ve.
        this.setState({loadError: null, loading: true}, () => this.updateConnectionsInformation());
    }

    updateConnectionsInformation() {
        new APIClient(API_URL + 'agency/connections/').call('GET').then((data) => {
            if (data.status === 'ERROR_NO_AGENCY') {
                alert(data['error_message']);
                this.redirectToAgencyRegisterPage()
            } else if (data.status === 'OK') {
                this.setState({
                    lookerCCEnabled: data.data.looker_cc_enabled,
                    dataNodeEnabled: data.data.data_node_enabled,
                    lookerCCApiKey: data.data.looker_cc_api_key,
                    dataNodeApiKey: data.data.data_node_api_key,
                    loading: false
                });
            } else {
                this.setState({loadError: 'load'});
            }
        }).catch(() => this.setState({loadError: 'load'}));
    }

    render() {
        return (
            <section className="section-content">
                <AgencyHeader/>
                <AgencyTabs activeTab="-sixth"/>
                <ConnectionsSubTabs activeSubTab={CONNECTIONS_TAB_EXPORT}/>
                {this.state.loadError !== null ? this.renderLoadError() : this.renderExports()}
                {/* Fuera de la rama del error: la tarjeta no usa nada de `agency/connections/` (su
                    gate es el módulo) y en la 2.1 es la ÚNICA puerta a las planillas, porque el
                    link salió del menú. Un GET caído no puede dejar sin camino a `/sheets-sync`
                    (review C-rec-fe-menu-03). */}
                <SheetsExportCard/>
            </section>
        )
    }

    // Mismo texto que la pestaña Plataformas: es el mismo pedido (`agency/connections/`).
    renderLoadError() {
        const {t} = this.props;
        return (
            <LoadErrorState title={t('agency_connections_configuration.load_error')}
                            text={t('agency_connections_configuration.load_error_explanation')}
                            retryLabel={t('agency_connections_configuration.load_error_retry')}
                            onRetry={this.retryLoad}/>
        );
    }

    renderExports() {
        return (
            <>
                <div className="section-body -wider h-items" style={{paddingBottom:'0em'}}>
                    {this.renderLookerCCComponent()}
                </div>
                <div className="section-body -wider h-items" style={{paddingBottom:'0em'}}>
                    {this.renderDataNodeComponent()}
                </div>
            </>
        );
    }

    renderLookerCCComponent() {
        return <LookerCCComponent apiKey={this.state.lookerCCApiKey}
                                  loading={this.state.loading}
                                  hasAccessToModule={this.state.lookerCCEnabled}
        />;
    }

    renderDataNodeComponent() {
        return <DataNodeComponent apiKey={this.state.dataNodeApiKey}
                                  loading={this.state.loading}
                                  hasAccessToModule={this.state.dataNodeEnabled}
        />;
    }
}

/**
 * La exportación a planillas, como tarjeta que lleva a su pantalla (`/sheets-sync`): la lista de
 * sincronizaciones y su editor siguen viviendo allá. Mismo gate que tenía su entrada en el menú
 * (el módulo `SHEETS_SYNC`): sin el módulo, la tarjeta no existe.
 */
export function SheetsExportCard() {
    const {t} = useTranslation();
    const {modules} = useUserModules();
    if (!(modules || []).includes(AccessService.MODULE_SHEETS_SYNC)) return null;

    return (
        <div className="section-body -wider h-items" style={{paddingBottom:'0em'}}>
            <div className="section-body-item -wider box" data-testid="agency-export-sheets">
                <header className="box-header">
                    <h2 className="box-title">
                        <ChannelLogo width={35} height={35} channel={SHEET_CHANNEL}/>
                        {t('agency_connections.export.sheets_title')}
                    </h2>
                </header>
                <div className="form-row">
                    <label>{t('agency_connections.export.sheets_text')}</label>
                </div>
                <div className="form-row">
                    <Link to="/sheets-sync">{t('agency_connections.export.sheets_cta')}</Link>
                </div>
            </div>
        </div>
    );
}

export default withRouterParams(withTranslation()(AgencyConnectionsExportContent));
